// Pencil-tool previews and comparison sheets with the real drawing code: the
// pencil stamp generator (pencilUtils), the graphite tip shader or the dry-media
// shader (dryMedia.js), and the paper tooth with its calibration
// (surfaceContact.js) - drawn in one small offscreen WebGL context.
//
// renderPencilPreview(): reference strokes (light / medium / heavy pressure,
// broad side shading, three passes) into a 2D canvas, cached, rendered when the
// browser is idle so drawing is never blocked.
// renderMediaSheet(): every medium on several papers (development and
// documentation; window.__vxPencilMediaSheet).

import { createSurface, physicalSurfaceOf } from '../../../canvas/paint-engine/surfaces/PaperModel';
import {
  calibrateProfile,
  contactProfileFor,
  drawingToothStrength,
  normaliseToothField,
  paperCalibration,
  paperToothTile,
} from '../../../canvas/paint-engine/integration/surfaceContact';
import { createProgram, IMPORTED_FRAGMENT, IMPORTED_VERTEX } from './canvasGpuCore';
import { getPencilBrushTipCanvas, getPencilDenseSegmentStamps } from './pencilUtils';
import { buildDryMediaVertices, createDryMediaRenderer, isDryMedium, mediumDensity } from './dryMedia';

let shared = null;

function getRenderer() {
  if (shared) return shared;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl', { premultipliedAlpha: true, preserveDrawingBuffer: true, antialias: false });
  if (!gl) return null;
  const graphite = createProgram(gl, IMPORTED_VERTEX, IMPORTED_FRAGMENT);
  const dry = createDryMediaRenderer(gl, createProgram);
  const quad = gl.createBuffer();
  const tooth = gl.createTexture();
  const tips = new Map();
  shared = { canvas, gl, graphite, dry, quad, tooth, tips, toothKey: '' };
  gl.canvas.addEventListener('webglcontextlost', () => { shared = null; });
  return shared;
}

const hex = (value) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(value || ''));
  const n = m ? parseInt(m[1], 16) : 0x26262c;
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

function uploadTooth(r, tooth) {
  const { gl } = r;
  const key = tooth ? `${tooth.key || ''}|${tooth.width}x${tooth.height}` : 'none';
  if (r.toothKey === key) return;
  r.toothKey = key;
  gl.bindTexture(gl.TEXTURE_2D, r.tooth);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  const bytes = tooth ? tooth.bytes : new Uint8Array([128]);
  const w = tooth ? tooth.width : 1;
  const h = tooth ? tooth.height : 1;
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, w, h, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, bytes);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  const pot = (v) => (v & (v - 1)) === 0;
  const wrap = tooth && tooth.repeat && pot(w) && pot(h) ? gl.REPEAT : gl.CLAMP_TO_EDGE;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
}

// Same uniforms as canvasGpuCore.applySurfaceContact.
function applyTooth(r, program, tooth, media, grade, tilt) {
  const { gl } = r;
  const profile = calibrateProfile(contactProfileFor({ tool: 'pen', preset: 'pencil', media, grade, tilt }), tooth ? tooth.calibration : null);
  const strength = tooth ? Math.max(0, Math.min(1, tooth.strength * profile.sensitivity)) : 0;
  gl.uniform1f(gl.getUniformLocation(program, 'u_toothStrength'), strength);
  const tile = tooth ? (tooth.repeat ? [tooth.width, tooth.height] : [tooth.width * (tooth.pxPerCell || 2), tooth.height * (tooth.pxPerCell || 2)]) : [1, 1];
  gl.uniform2f(gl.getUniformLocation(program, 'u_toothResolution'), tile[0], tile[1]);
  gl.uniform4f(gl.getUniformLocation(program, 'u_contact'), profile.base, profile.pressureReach, Math.max(0.05, profile.width), profile.floor);
  gl.activeTexture(gl.TEXTURE6);
  gl.bindTexture(gl.TEXTURE_2D, r.tooth);
  gl.uniform1i(gl.getUniformLocation(program, 'u_tooth'), 6);
  gl.activeTexture(gl.TEXTURE0);
  gl.uniform1f(gl.getUniformLocation(program, 'u_selectionEnabled'), 0);
}

function tipTexture(r, stamp) {
  const { gl } = r;
  const source = getPencilBrushTipCanvas(Math.max(1, Math.ceil(Number(stamp.size) || 1)), Number(stamp.stampSeed) || 0);
  if (!source) return null;
  let texture = r.tips.get(source);
  if (texture) return texture;
  texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  if (r.tips.size > 200) r.tips.clear();
  r.tips.set(source, texture);
  return texture;
}

// Graphite: the original tip-texture stamping (as canvasGpuCore.drawPencilDabs).
function drawGraphite(r, stamps, options, tooth, width, height, media) {
  const { gl, graphite } = r;
  gl.useProgram(graphite);
  applyTooth(r, graphite, tooth, media, (Number(options.pencilGrade) || 0) / 8, 0);
  gl.uniform2f(gl.getUniformLocation(graphite, 'u_resolution'), width, height);
  const c = hex(options.color);
  gl.uniform3f(gl.getUniformLocation(graphite, 'u_colour'), c[0], c[1], c[2]);
  gl.bindBuffer(gl.ARRAY_BUFFER, r.quad);
  const position = gl.getAttribLocation(graphite, 'a_position');
  const uv = gl.getAttribLocation(graphite, 'a_uv');
  gl.enableVertexAttribArray(position);
  gl.enableVertexAttribArray(uv);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
  gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);
  for (const stamp of stamps) {
    const tex = tipTexture(r, stamp);
    if (!tex) continue;
    const size = Math.max(0.5, Number(stamp.size) || 1);
    const hw = size * Math.max(0.05, Number(stamp.scaleX) || 1) * 0.5;
    const hh = size * Math.max(0.05, Number(stamp.scaleY) || 1) * 0.5;
    const a = Number(stamp.rotation) || 0;
    const co = Math.cos(a); const si = Math.sin(a);
    const { x, y } = stamp.point;
    const v = [];
    for (const [lx, ly, u, w] of [[-hw, -hh, 0, 0], [hw, -hh, 1, 0], [-hw, hh, 0, 1], [-hw, hh, 0, 1], [hw, -hh, 1, 0], [hw, hh, 1, 1]]) v.push(x + lx * co - ly * si, y + lx * si + ly * co, u, w);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(v), gl.STREAM_DRAW);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(gl.getUniformLocation(graphite, 'u_tip'), 0);
    gl.uniform1f(gl.getUniformLocation(graphite, 'u_opacity'), Math.max(0.001, Math.min(1, Number(stamp.alpha) || 0)));
    // Graphite's contact pressure stays what drawing uses (stamps carry none).
    gl.uniform1f(gl.getUniformLocation(graphite, 'u_contactPressure'), 0.6);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
  gl.disableVertexAttribArray(position);
  gl.disableVertexAttribArray(uv);
}

// One stroke along `points` (each { x, y, pressure }).
function stroke(r, points, options, tooth, width, height, seed, legacy) {
  const media = options.surfaceMedia;
  let stamps = [];
  let index = 0;
  for (let i = 1; i < points.length; i += 1) {
    const res = getPencilDenseSegmentStamps(points[i - 1], points[i], options, seed, index, i === 1);
    index = res.nextDabIndex;
    stamps = stamps.concat(res.stamps);
  }
  if (!stamps.length) return;
  if (legacy || !isDryMedium(media.pencil)) {
    drawGraphite(r, stamps, options, tooth, width, height, media);
    return;
  }
  const { gl, dry } = r;
  const orientation = options.pencilOrientation || { elevation: 90 };
  const tilt = (90 - Math.max(15, Math.min(90, Number(orientation.elevation) || 90))) / 75;
  const built = buildDryMediaVertices(stamps, { medium: media.pencil, size: options.size, opacity: options.opacity, tilt }, { seed });
  gl.useProgram(dry.program);
  applyTooth(r, dry.program, tooth, media, (Number(options.pencilGrade) || 0) / 8, tilt);
  dry.draw(built.vertices, built.count, { colour: hex(options.color), medium: media.pencil, density: mediumDensity(media.pencil, (Number(options.pencilGrade) || 0) / 8), width, height });
}

const wave = (x0, x1, y, pressure, amp = 3, step = 4) => {
  const pts = [];
  for (let x = x0; x <= x1; x += step) pts.push({ x, y: y + Math.sin(x / 17) * amp, pressure });
  return pts;
};

// Reference strokes for one medium into the current framebuffer region.
function referenceStrokes(r, { media, grade = 0, size = 10, color, tooth, width, height, ox = 0, oy = 0, legacy = false }) {
  const base = { size, softness: 0, opacity: 1, pencilGrade: grade * 8, color, surfaceMedia: media };
  const upright = { ...base, pencilOrientation: { azimuth: 0, elevation: 90 } };
  const side = { ...base, size: size * 1.6, pencilOrientation: { azimuth: 0, elevation: 20 } };
  const w = width - ox;
  const rows = [[0.2, 0.16], [0.5, 0.33], [0.9, 0.5]];
  rows.forEach(([p, fy], k) => stroke(r, wave(ox + 8, ox + w * 0.58, oy + height * fy, p), upright, tooth, width, height + oy, 11 + k, legacy));
  // Broad side shading: back-and-forth hatching with the side of the tip.
  const hatch = [];
  for (let k = 0; k < 9; k += 1) hatch.push({ x: ox + w * 0.64 + (k % 2) * 6 + k * 9, y: oy + height * (k % 2 ? 0.12 : 0.55), pressure: 0.55 });
  stroke(r, hatch, side, tooth, width, height + oy, 31, legacy);
  // Three passes at medium pressure.
  for (let pass = 0; pass < 3; pass += 1) stroke(r, wave(ox + 8, ox + w * 0.58, oy + height * 0.76, 0.5), upright, tooth, width, height + oy, 41 + pass, legacy);
}

function paperBackground(ctx, w, h, tooth, tone = 240, x0 = 0, y0 = 0) {
  const image = ctx.createImageData(w, h);
  const per = tooth ? (tooth.pxPerCell || 2) : 1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let z = 0;
      if (tooth) {
        const cx = Math.floor((x + x0) / per); const cy = Math.floor((y + y0) / per);
        const i = (tooth.repeat ? (cy % tooth.height) * tooth.width + (cx % tooth.width) : Math.min(tooth.height - 1, cy) * tooth.width + Math.min(tooth.width - 1, cx));
        z = (tooth.bytes[i] / 255 - 0.5) * 6;
      }
      const v = tone + Math.max(-1, Math.min(1, z / 3)) * 7 * (tooth ? Math.min(1, tooth.strength * 1.5) : 0);
      const o = 4 * (y * w + x);
      // Dark papers are a cool grey-green (as for chalk); light ones are cream.
      if (tone < 128) { image.data[o] = v - 8; image.data[o + 1] = v + 4; image.data[o + 2] = v; } else { image.data[o] = v; image.data[o + 1] = v - 2; image.data[o + 2] = v - 8; }
      image.data[o + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
}

// Draws the reference strokes for `media` onto `target` (a 2D canvas).
export function renderPencilPreviewNow(target, { media, grade = 0, size = 10, color = '#26262c', tooth = null, legacy = false }) {
  const r = getRenderer();
  if (!r || !target) return false;
  const { gl, canvas } = r;
  const width = target.width;
  const height = target.height;
  canvas.width = width;
  canvas.height = height;
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  uploadTooth(r, tooth);
  referenceStrokes(r, { media, grade, size, color, tooth, width, height, legacy });
  const ctx = target.getContext('2d');
  // Light colours (chalk, white pastel) are shown on a dark paper.
  const [cr, cg, cb] = hex(color);
  paperBackground(ctx, width, height, tooth, 0.2126 * cr + 0.7152 * cg + 0.0722 * cb > 0.6 ? 72 : 240);
  ctx.drawImage(canvas, 0, 0);
  return true;
}

const cache = new Map();
// Idle, cached version for the tool panel.
export function renderPencilPreview(target, params) {
  const key = JSON.stringify([params.media && params.media.pencil, params.grade, params.size, params.color, params.tooth ? params.tooth.key : '', params.tooth ? params.tooth.strength : 0, params.legacy, target.width, target.height]);
  const hit = cache.get(key);
  const ctx = target.getContext('2d');
  if (hit) { ctx.putImageData(hit, 0, 0); return () => {}; }
  let cancelled = false;
  const run = () => {
    if (cancelled) return;
    if (!getRenderer()) {
      if (typeof params.onUnavailable === 'function') params.onUnavailable();
      return;
    }
    if (renderPencilPreviewNow(target, params)) {
      if (cache.size > 40) cache.delete(cache.keys().next().value);
      cache.set(key, ctx.getImageData(0, 0, target.width, target.height));
    }
  };
  const handle = typeof requestIdleCallback === 'function' ? requestIdleCallback(run, { timeout: 400 }) : setTimeout(run, 30);
  return () => {
    cancelled = true;
    if (typeof cancelIdleCallback === 'function') cancelIdleCallback(handle); else clearTimeout(handle);
  };
}

// Paper as the Canvas core would use it for drawing (for the sheet).
export function toothForPaper(preset, width = 512, height = 256) {
  const physics = physicalSurfaceOf(createSurface(Math.ceil(width / 2), Math.ceil(height / 2), preset, 7, {}), 'physical', 1);
  const norm = normaliseToothField(physics.heightField);
  const calibration = norm.sd > 1e-5 ? paperCalibration(preset) : null;
  const tile = calibration ? paperToothTile(preset, 1) : null;
  const strength = drawingToothStrength({ tooth: physics.preset.tooth, friction: physics.preset.friction ?? physics.preset.tooth, sd: norm.sd }, calibration);
  return tile
    ? { bytes: tile.bytes, width: tile.width, height: tile.height, strength, calibration, repeat: true, pxPerCell: 1, key: `sheet-${preset}` }
    : { bytes: norm.bytes, width: Math.ceil(width / 2), height: Math.ceil(height / 2), strength, calibration: null, repeat: false, pxPerCell: 2, key: `sheet-${preset}` };
}

export const SHEET_MEDIA = ['graphite', 'mechanical', 'coloured', 'charcoal', 'pastel', 'chalk', 'crayon'];
export const SHEET_PAPERS = ['smooth_drawing', 'hot_press', 'cold_press', 'handmade', 'pastel', 'canvas_fine'];
const MEDIA_COLOUR = { graphite: '#2a2a30', mechanical: '#2a2a30', coloured: '#2e5aa8', charcoal: '#1c1a1a', pastel: '#c0503c', chalk: '#e9e4d6', crayon: '#c23a2e' };

// Every medium (columns) on every paper (rows), each cell at 1:1. Chalk is
// drawn in its usual light colour, so its cells show a dark paper.
export function renderMediaSheet({ cellWidth = 260, cellHeight = 120, legacy = false, papers = SHEET_PAPERS, media = SHEET_MEDIA } = {}) {
  const sheet = document.createElement('canvas');
  sheet.width = media.length * (cellWidth + 6);
  sheet.height = papers.length * (cellHeight + 6);
  const ctx = sheet.getContext('2d');
  ctx.fillStyle = '#2a2a2e';
  ctx.fillRect(0, 0, sheet.width, sheet.height);
  const cell = document.createElement('canvas');
  cell.width = cellWidth;
  cell.height = cellHeight;
  papers.forEach((paper, row) => {
    const tooth = toothForPaper(paper, cellWidth, cellHeight);
    media.forEach((m, col) => {
      renderPencilPreviewNow(cell, { media: { pencil: m, pen: 'pen', eraser: 'soft' }, grade: 0, size: 10, color: MEDIA_COLOUR[m], tooth, legacy });
      ctx.drawImage(cell, col * (cellWidth + 6), row * (cellHeight + 6));
    });
  });
  return sheet;
}

if (typeof window !== 'undefined') {
  // Development: window.__vxPencilMediaSheet({ legacy }) -> canvas.
  window.__vxPencilMediaSheet = (options) => renderMediaSheet(options);
}
